import { useCallback, useMemo } from 'react';
import ReportsService from '~/api-services/ReportsService';
import {
    Cash,
    Commission,
    Interest,
    Order,
    PageIdRequestV2,
    ReportRequestSpecV2,
    Result,
} from '~/api-services/ReportsService/types';
import {
    cashExportColumns,
    commissionsExportColumns,
    interestExportColumns,
    ordersExportColumns,
    resultExportColumns,
} from '~/lib/reportExportColumns';
import { accountBalanceSelector } from '~/state/selectors';
import { useAIStrategyEnabled } from './useAIStrategyEnabled';
import { useAppSelector } from './useAppSelector';
import { useCsvExportEnabled } from './useCsvExportEnabled';
import { ExportColumn, ExportPage, useReportExport } from './useReportExport';

const fileDate = (timestamp: number) => {
    const d = new Date(timestamp);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

type ExportEndpoint<T> = (
    request: { spec: ReportRequestSpecV2; lastPageId?: PageIdRequestV2 | null; pageSize: number },
    signal?: AbortSignal
) => Promise<{ data: { returnData: ExportPage<T> } }>;

const useCsvExport = <T>(
    name: string,
    endpoint: ExportEndpoint<T>,
    buildColumns: (aiEnabled: boolean) => ExportColumn<T>[],
    reportSpec: ReportRequestSpecV2 | null
) => {
    const enabled = useCsvExportEnabled();
    const aiEnabled = useAIStrategyEnabled();
    const currency = useAppSelector(accountBalanceSelector)?.currency || '';

    const columns = useMemo(() => buildColumns(aiEnabled), [buildColumns, aiEnabled]);

    const fetchPage = useCallback(
        async (lastPageId: PageIdRequestV2 | null, pageSize: number, signal: AbortSignal) => {
            const { data } = await endpoint({ spec: reportSpec, lastPageId, pageSize }, signal);
            return data.returnData;
        },
        [endpoint, reportSpec]
    );

    const filename = useCallback(
        () => `${name}_${fileDate(reportSpec?.from)}_${fileDate(reportSpec?.to)}.csv`,
        [name, reportSpec]
    );

    return useReportExport<T>({
        enabled: enabled && !!reportSpec,
        fetchPage,
        columns,
        context: { currency },
        filename,
    });
};

export const useResultCsvExport = (reportSpec: ReportRequestSpecV2 | null) =>
    useCsvExport<Result>(
        'result',
        (request, signal) => ReportsService.getReportResultsExport(request, signal),
        resultExportColumns,
        reportSpec
    );

export const useCashCsvExport = (reportSpec: ReportRequestSpecV2 | null) =>
    useCsvExport<Cash>(
        'cash',
        (request, signal) => ReportsService.getReportCashExport(request, signal),
        cashExportColumns,
        reportSpec
    );

export const useOrdersCsvExport = (reportSpec: ReportRequestSpecV2 | null) =>
    useCsvExport<Order>(
        'orders',
        (request, signal) => ReportsService.getReportOrdersExport(request, signal),
        ordersExportColumns,
        reportSpec
    );

export const useCommissionsCsvExport = (reportSpec: ReportRequestSpecV2 | null) =>
    useCsvExport<Commission>(
        'commissions',
        (request, signal) => ReportsService.getReportCommissionsExport(request, signal),
        commissionsExportColumns,
        reportSpec
    );

export const useInterestCsvExport = (reportSpec: ReportRequestSpecV2 | null) =>
    useCsvExport<Interest>(
        'interest-swap',
        (request, signal) => ReportsService.getReportInterestsExport(request, signal),
        interestExportColumns,
        reportSpec
    );
